import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './PageHeader.css';

/**
 * The display header a page opens with.
 *
 * A breadcrumb trail over a big title, with an optional cluster of actions
 * sitting on the title's baseline, all closed off by a hairline rule. Every
 * page uses this rather than its own arrangement, so the trail is always in
 * the same place and says where you are the same way.
 *
 * `crumbs` is the trail from the top down, e.g.
 *   [{ label: 'Home', to: '/' }, { label: 'Timesheet' }]
 * The last one is where you are and never links.
 *
 * `count` is how big the collection on the page is, when the page is a list of
 * something. It rides in a bubble beside the title rather than in prose.
 *
 * `note` is a fact about what is on screen that a bubble cannot hold — "3 of 8
 * awaiting review". It sits beside the title in the muted body size.
 *
 * Back is the header's own, always last in the cluster so it is in the same
 * place on every page. It goes wherever the trail came from — the crumb above
 * the current one — unless `backTo` or `onBack` says otherwise. `showBack`
 * turns it off for a page with nowhere to go back to.
 */
export default function PageHeader({
  crumbs = [],
  icon,
  title,
  count = null,
  note = null,
  actions = null,
  showBack = true,
  backTo = null,
  backLabel = 'Back',
  onBack = null,
}) {
  const navigate = useNavigate();

  const parentCrumb = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;
  const backTarget = backTo || parentCrumb?.to || '/';
  const handleBack = onBack || (() => navigate(backTarget));

  return (
    <header className="bdc-pageheader">
      <div className="bdc-pageheader__main">
        {crumbs.length > 0 ? (
          <nav className="bdc-crumbs" aria-label="Breadcrumb">
            {crumbs.map((crumb, idx) => {
              const isCurrent = idx === crumbs.length - 1;
              return (
                <React.Fragment key={crumb.to || crumb.label}>
                  {idx > 0 ? (
                    <i className="bi bi-chevron-right bdc-crumb-sep" aria-hidden="true" />
                  ) : null}
                  {isCurrent || !crumb.to ? (
                    <span
                      className={isCurrent ? 'bdc-crumb-current' : undefined}
                      aria-current={isCurrent ? 'page' : undefined}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link className="bdc-crumb-link" to={crumb.to}>{crumb.label}</Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        ) : null}

        <div className="bdc-pageheading">
          <h1 className="bdc-pagetitle">
            <span className="bdc-pagetitle__inner">
              {icon ? <i className={`bi ${icon}`} aria-hidden="true" /> : null}
              {title}
            </span>
          </h1>
          {count !== null && count !== undefined ? (
            <span className="bdc-count">{count}</span>
          ) : null}
          {note ? <span className="bdc-pageheader__note">{note}</span> : null}
        </div>
      </div>

      {actions || showBack ? (
        <div className="bdc-pageactions">
          {actions}
          {showBack ? (
            <button type="button" className="bdc-btn" onClick={handleBack}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              {backLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
